import { ChevronRight } from "lucide-react";
import { Mono } from "@/components/site/primitives";
import { cn } from "@/lib/utils";

const StepText = ({ s }) => (
  <>
    <p className="font-display text-base font-bold">{s.label}</p>
    <p className="mt-1 text-sm text-o-dim">{s.text}</p>
  </>
);

const Sequence = ({ steps, hue }) => (
  <ol className="mt-5 grid gap-3 md:grid-cols-3">
    {steps.map((s, i) => (
      <li key={s.label} className="relative border border-o-line bg-o-bg p-4">
        <span aria-hidden className="block h-1 w-10" style={{ background: hue }} />
        <div className="mt-4"><StepText s={s} /></div>
        {i < steps.length - 1 && <ChevronRight aria-hidden className="absolute -right-[14px] top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-o-dim md:block" />}
      </li>
    ))}
  </ol>
);

const Timeline = ({ steps, hue }) => (
  <ol className="relative mt-6 grid gap-6 md:grid-cols-4 md:gap-4">
    <span aria-hidden className="absolute bottom-2 left-[7px] top-2 w-px bg-o-strong md:bottom-auto md:left-0 md:right-0 md:top-[7px] md:h-px md:w-auto" />
    {steps.map((s, i) => (
      <li key={s.label} className="relative pl-8 md:pl-0 md:pt-8">
        <span aria-hidden className="absolute left-0 top-0 h-[15px] w-[15px] rounded-full border-2 bg-o-bg"
          style={{ borderColor: hue, background: i === steps.length - 1 ? hue : undefined }} />
        <StepText s={s} />
      </li>
    ))}
  </ol>
);

const Threshold = ({ steps, hue }) => (
  <ol className="mt-5 grid gap-3 md:grid-cols-[1fr_1.1fr_1fr]">
    <li className="border border-dashed border-o-strong p-4"><StepText s={steps[0]} /></li>
    <li className="border-l-[6px] border-o-line bg-o-bg p-4 md:border-l-0 md:border-t-[6px]" style={{ borderColor: hue }}><StepText s={steps[1]} /></li>
    <li className="border border-o-line bg-o-bg p-4"><StepText s={steps[2]} /></li>
  </ol>
);

const COUNTS = [6, 3, 1];
const Participation = ({ steps, hue }) => (
  <div className="mt-5 grid gap-5 md:grid-cols-3">
    {steps.map((s, i) => (
      <div key={s.label}>
        <div className="flex min-h-[72px] flex-wrap content-start gap-1.5">
          {Array.from({ length: COUNTS[i] }).map((_, j) => (
            <span key={j} aria-hidden className={cn("border-2 bg-o-bg", COUNTS[i] === 1 ? "h-[72px] w-full" : "h-8 w-8")} style={{ borderColor: hue }} />
          ))}
        </div>
        <div className="mt-3"><StepText s={s} /></div>
      </div>
    ))}
  </div>
);

const VIEWS = { sequence: Sequence, timeline: Timeline, threshold: Threshold, participation: Participation };

export const CapabilitySignature = ({ cap }) => {
  const View = VIEWS[cap.signature.type];
  return (
    <div data-testid={`signature-${cap.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Mono accent>{cap.signature.title}</Mono>
        <span className="draft-tag">Conceptual diagram · not a product interface</span>
      </div>
      <View steps={cap.signature.steps} hue={cap.hue} />
    </div>
  );
};
